<template>
  <v-card outlined color="grey lighten-4" tile class="ml-3 mt-0 mb-0">
    <div style="font-size: 14px;">
        <DocumentListToolbar
            :totalResults="filteredSortedDocumentList.length"
            :filteredResults="documentList.length - filteredSortedDocumentList.length"
            :selectedDocuments="selectedDocuments"
            :canEdit="canEdit"
            :canDelete="canDelete"
            :hasGroupedItems="groupItems.length > 0"
            :hasSortedItems="sortItems.length > 0"
            :hasFilteredItems="filterItems.length > 0"
            @on-refresh-click="refreshResults"
            @on-select-all-change="selectAllDocuments"
            @on-validate-click="onValidate"
            @on-refuse-click="onRefuse"
            @on-filter-click="onFilter"
            @on-groupBy-click="onGroup"
            @on-sort-click="onSort"
            @on-configure-fields-order-click="onConfigureFieldsOrder"
            @on-export-selected-click="onExportSelected"
            @on-delete-selected-click="onDeleteSelected"
            @on-internal-distribute-selected-click="onInternalDistributeSelected"
            @on-external-distribute-selected-click="onExternalDistributeSelected"
            @on-edit-selected-click="onEditSelected"
            @on-download-selected-click="onDownloadSelected"
            @on-load-plugin-click="loadPlugin"
        />
        <v-container
            class="blue white--text pt-2 pb-1 ma-0 d-flex align-center"
            style="font-size: 12px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;"
            >
            <span class="text-truncate" style="overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 100%;">
                {{ searchName }}
            </span>

            <v-tooltip top z-index="1000" v-if="!loading && searchType == 'adv-search'">
            <template v-slot:activator="{ on }">
                <v-btn icon small v-on="on" @click.prevent="editSearch">
                <v-icon small color="light-blue lighten-4" class="pa-0">mdi-pencil</v-icon>
                </v-btn>
            </template>
            <span>{{ $t("Edit") }}</span>
            </v-tooltip>
        </v-container>
    </div>
      <!-- Loading State -->
    <v-row justify="center" align="center" v-if="loading">
        <div class="text-center">
            <v-progress-circular
            class="my-4"
            :size="50"
            width="5"
            color="primary"
            indeterminate
            />
            <p class="display-1">{{ $t("Loading") }}...</p>
        </div>
    </v-row>

      <!-- Content Area -->
    <v-card-text class="ma-0 pa-0 overflow-y-auto" style="max-height: 81vh;">
        <!-- Table View -->
        <template v-if="!loading && useTableView">
            <TableViewContainer
                ref="tableViewContainer"
                :groupedDocumentList="paginatedGroupedDocumentList"
                :unGroupedDocumentList="paginatedDocumentList"
                :allDocumentList="allDocumentList"
                :noDocumentsFound="filteredSortedDocumentList.length === 0"
                :defaultVisibleFields="defaultTableVisibleFields"
                :selectedDocument="selectedDocument"
                :selectedDocuments="selectedDocuments"
                :totalItems="totalItems"
                :prevSelectedContentType="selectedContentType"
                :latestCTVersionFields="latestCtVersionFields"
                @on-content-click="loadPdf"
                @on-selection-change="onDocumentSelected"
                @on-version-change="reloadDocument"
                @on-version-removed="onVersionDeleted"
                @on-document-removed="removeDocumentFromList"
                @on-document-blocked="onDocumentBlocked"
                @on-reception-confirmed="onReceptionConfirmed"
                @on-distribution-ended="onDistributionEnded"
                @on-edit-click="editDocument"
                @on-flow-diagram-click="showFlowDiagram"
                @on-sign-click="signDocument"
                @on-load-plugin-click="loadPlugin"
                @on-page-change="handlePageChange"
                @on-retry-process="onRetryProcess"
                @on-table-filter="onFilter"
                @on-table-sort="onSortTableViewHeader"
            />
        </template>

        <!-- Card View -->
        <template v-if="!loading && !useTableView">
            <!-- Ungrouped Documents -->
            <Lazy
                v-for="document in ungroupedDocumentList"
                :key="`${document.ObjectID}-${renderTrigger}`"
                :unrender="true"
            >
                <DocumentItem
                :documentDto="document"
                :isSelected="document.ObjectID == selectedDocument"
                :fieldsCount="defaultVisibleFields"
                :selectedDocuments="selectedDocuments"
                @on-version-change="reloadDocument"
                @on-selection-change="onDocumentSelected"
                @on-version-removed="onVersionDeleted"
                @on-document-removed="removeDocumentFromList"
                @on-expand-change="onDocumentExtend"
                @on-document-blocked="onDocumentBlocked"
                @on-reception-confirmed="onReceptionConfirmed"
                @on-distribution-ended="onDistributionEnded"
                @on-edit-click="editDocument"
                @on-flow-diagram-click="showFlowDiagram"
                @on-sign-click="signDocument"
                @on-load-plugin-click="loadPlugin"
                @on-content-click="loadPdf"
                @on-retry-process="onRetryProcess"
                />
            </Lazy>

            <!-- Grouped Documents -->
            <template v-if="groupedDocumentList && groupedDocumentList.length > 0">
                <Lazy
                    v-for="(groupedItem, index) in groupedDocumentList"
                    :key="`${groupedItem.title}-${index}-${renderTrigger}`"
                    :min-height="20"
                    :unrender="true"
                >
                    <v-expansion-panels multiple accordion flat tile>
                        <GroupedDocumentItem
                        :groupedItem="groupedItem"
                        :selectedDocument="selectedDocument"
                        :defaultVisibleFields="defaultVisibleFields"
                        :selectedDocuments="selectedDocuments"
                        @on-version-change="reloadDocument"
                        @on-selection-change="onDocumentSelected"
                        @on-version-removed="onVersionDeleted"
                        @on-document-removed="removeDocumentFromList"
                        @on-document-blocked="onDocumentBlocked"
                        @on-reception-confirmed="onReceptionConfirmed"
                        @on-distribution-ended="onDistributionEnded"
                        @on-edit-click="editDocument"
                        @on-flow-diagram-click="showFlowDiagram"
                        @on-sign-click="signDocument"
                        @on-load-plugin-click="loadPlugin"
                        @on-content-click="loadPdf"
                        />
                    </v-expansion-panels>
                </Lazy>
            </template>
        </template>

        <!-- No Data State -->
        <base-no-data
        v-if="filteredSortedDocumentList.length == 0 && !loading"
        :text="$t('NoDocumentsFound')"
        icon="mdi-file-document-outline"
        />
    </v-card-text>

        <!-- Modals -->
    <base-modal
      :show="showGroup"
      :title="$t('ToGroup')"
      :okText="$t('ToGroup')"
      :closeText="$t('Close')"
      maxWidth="80%"
      @on-confirm="group()"
      @on-cancel="hideGroup()"
    >
      <GroupModal
        :ref="groupModalKey"
        :fieldList="filteredFieldList"
        :showContentTypeWarning="showContentTypeWarning"
        @on-grouped="onGrouped"
      />
    </base-modal>

    <base-modal
      :show="showFilter"
      :title="$t('ToFilter')"
      :okText="$t('ToFilter')"
      :closeText="$t('Close')"
      maxWidth="50%"
      @on-confirm="filter()"
      @on-cancel="hideFilter()"
    >
      <FilterModal
        :ref="filterModalKey"
        :fieldList="fieldList"
        :clear="clearFilter"
        @on-filtered="onFiltered"
      />
    </base-modal>

    <base-modal
      :show="showSort"
      :title="$t('Sort')"
      :okText="$t('Sort')"
      :closeText="$t('Close')"
      @on-confirm="sort()"
      @on-cancel="hideSort()"
    >
      <SortModal
        :ref="sortModalKey"
        :fieldList="filteredFieldList"
        :showContentTypeWarning="showContentTypeWarning"
        @on-sorted="onSorted"
      />
    </base-modal>

    <DistributionModal
      :ref="distributionModalKey"
      @on-distributed="refreshResults"
    />

    <ValidationModal
      :ref="validationModalKey"
      @on-validated="refreshResults"
    />

    <AdvSearchModal :ref="advSearchModalKey" />

    <DocumentModal
      :ref="documentModalKey"
      @on-document-updated="onDocumentUpdated"
    />

    <MultipleEditionModal :ref="multipleEditionModalKey" />

    <SignatureModal
      :ref="signatureModalKey"
      @on-sign="onSign"
    />

    <AttachmentModal
      :ref="attachmentModalKey"
      @on-attachment-saved="onAttachmentSaved"
    />

    <FlowDiagramModal :ref="flowDiagramModalKey" />

    <PluginLoaderModal
      :ref="pluginLoaderModalKey"
      @on-refresh-results="refreshResults"
    />

    <FieldOrderConfigModal
        :contentTypes="contentTypesWithFields"
        :existingConfigs="userColumnsOrderConfig"
        :ref="fieldOrderConfigModalKey"
    />
  </v-card>
</template>

  <script>
import DocumentListToolbar from '@/components/search/results/list/DocumentListToolbar';
import DocumentItem from '@/components/search/results/list/item/DocumentItem';
import GroupModal from '@/components/search/results/GroupModal.vue';
import FilterModal from '@/components/search/results/FilterModal.vue';
import SortModal from '@/components/search/results/SortModal.vue';
import DistributionModal from '@/components/search/results/DistributionModal.vue';
import ValidationModal from '@/components/search/results/ValidationModal.vue';
import PluginLoaderModal from '@/components/search/results/PluginLoaderModal.vue';
import StandardFieldCode from '@/ecm/variables/StandardFieldCode';
import FieldType from '@/ecm/variables/FieldType';
import SearchType from '@/ecm/variables/SearchType';
import SearchService from '@/services/SearchService';
import DocumentService from '@/services/DocumentService';
import Utils from '@/ecm/common/Utils';
import { EventBus } from '@/eventBus.js';
import DocumentModal from '@/components/document/documentModal/DocumentModal';
import MultipleEditionModal from '@/components/document/documentModal/MultipleEditionModal';
import AttachmentModal from '@/components/search/results/list/item/actions/attachment/AttachmentModal';
import FlowDiagramModal from '@/components/administration/flow/FlowDiagramModal.vue';
import SignatureModal from '@/components/search/results/list/item/actions/signature/SignatureModal';
import VueAutoVirtualScrollList from '@/components/search/results/list/VirtualListScroll.js';
import GroupedDocumentItem from '@/components/search/results/list/item/GroupedDocumentItem';
import GroupItem from '@/ecm/models/search/GroupItem';
import SortItem from '@/ecm/models/search/SortItem';
import BasketService from '@/services/BasketService';
import AdvSearchModal from '@/components/search/advSearch/AdvSearchModal';
import UserSearch from '@/ecm/models/UserSearch';
import Lazy from './Lazy.vue';
import TableViewContainer from '@/components/search/results/list/tableView/TableViewContainer.vue';
import UserSearchService from '@/services/UserSearchService';
import SortType from '@/ecm/variables/SortType';
import FieldOrderConfigModal from '@/components/search/results/list/FieldOrderConfigModal.vue';

export default {
    props: {
        searchType: {
            type: String,
            required: true,
        },
        searchPattern: {
            type: String,
            required: true,
        },
    },
    components: {
        Lazy,
        DocumentListToolbar,
        DocumentItem,
        GroupedDocumentItem,
        GroupModal,
        FilterModal,
        SortModal,
        DistributionModal,
        ValidationModal,
        DocumentModal,
        MultipleEditionModal,
        AttachmentModal,
        FlowDiagramModal,
        SignatureModal,
        PluginLoaderModal,
        VueAutoVirtualScrollList,
        AdvSearchModal,
        TableViewContainer,
        FieldOrderConfigModal
    },
    created() {
        this.refreshResults();
    },
    data() {
        return {
            documentList: [],
            groupItems: [],
            filterItems: [],
            sortItems: [],
            loading: false,
            selectedDocuments: [],
            selectedDocument: -1,
            searchName: '',
            defaultVisibleFields: 4,
            defaultTableVisibleFields: 4,
            showFilter: false,
            showSort: false,
            showGroup: false,
            canEdit: true,
            canDelete: true,
            groupModalKey: 'groupModalKey',
            filterModalKey: 'filterModalKey',
            sortModalKey: 'sortModalKey',
            distributionModalKey: 'distributionModalKey',
            validationModalKey: 'validationModalKey',
            documentModalKey: 'documentEditionModal',
            multipleEditionModalKey: 'multipleEditionModal',
            attachmentModalKey: 'attachmentModalDocList',
            flowDiagramModalKey: 'flowDiagramModalKey',
            signatureModalKey: 'signatureModal',
            pluginLoaderModalKey: 'pluginLoaderModalKey',
            advSearchModalKey: 'advSearchModal',
            fieldOrderConfigModalKey: 'fieldOrderConfigModalKey',
            groupStr: '',
            sortStr: '',
            contentTypeIDs: [],
            showContentTypeWarning: false,
            renderTrigger: 0,
            useTableView: this.$store.getters['account/user'].UseSearchResultsTableView,
            selectedContentType: null,
            allDocumentList: [],
            pagination: {
                page: 1,
                pageSize: 50,
            },
            clearFilter: false,
            userColumnsOrderConfig: this.$store.getters['account/userContentTypeResultsOrders'],
        };
    },
    computed: {
        paginatedDocumentList() {
            const filteredList = this.ungroupedDocumentList;

            const start = (this.pagination.page - 1) * this.pagination.pageSize;
            const end = start + this.pagination.pageSize;
            return filteredList.slice(start, end);
        },
        paginatedGroupedDocumentList() {
            if (!this.groupedDocumentList || this.groupedDocumentList.length === 0) return [];
            const start = (this.pagination.page - 1) * this.pagination.pageSize;
            const end = start + this.pagination.pageSize;
            return this.groupedDocumentList.slice(start, end);
        },
        totalItems() {
            if (this.groupedDocumentList && this.groupedDocumentList.length > 0) {
                return this.groupedDocumentList.length;
            }
            return this.documentList.length;
        },
        signatureModalInstance() {
            return this.$refs[this.signatureModalKey];
        },
        advSearchModalInstance() {
            return this.$refs[this.advSearchModalKey];
        },
        attachmentModalInstance() {
            return this.$refs[this.attachmentModalKey];
        },
        multipleEditionModalInstance() {
            return this.$refs[this.multipleEditionModalKey];
        },
        documentModalInstance() {
            return this.$refs[this.documentModalKey];
        },
        groupModalInstance() {
            return this.$refs[this.groupModalKey];
        },
        filterModalInstance() {
            return this.$refs[this.filterModalKey];
        },
        sortModalInstance() {
            return this.$refs[this.sortModalKey];
        },
        distributionModalInstance() {
            return this.$refs[this.distributionModalKey];
        },
        validationModalInstance() {
            return this.$refs[this.validationModalKey];
        },
        flowDiagramModalInstance() {
            return this.$refs[this.flowDiagramModalKey];
        },
        pluginLoaderModalInstance() {
            return this.$refs[this.pluginLoaderModalKey];
        },
        fieldOrderConfigModalInstance() {
            return this.$refs[this.fieldOrderConfigModalKey];
        },
        fieldList() {
            if (this.useTableView && this.filteredSortedDocumentList.length > 0) {
                const allowedTitles = new Set(this.latestCtVersionFields.map(f => f.Code));
                return this.documentList
                    .flatMap(doc => doc.Fields || [])
                    .filter(f => allowedTitles.has(f.Code));
            }

            return this.documentList.flatMap(doc => doc.Fields);
        },
        filteredFieldList() {
            if (this.searchType === SearchType.AdvancedSearch && this.contentTypeIDs.length > 1 && !this.useTableView) {
                const standardCodes = new Set(this.$store.getters['account/searchStandardFields'].map(sf => sf.Code));
                this.showContentTypeWarning = true;
                return this.filteredSortedDocumentList.flatMap(doc =>
                    doc.Fields.filter(field => standardCodes.has(field.Code))
                );
            }
            if (this.useTableView && this.filteredSortedDocumentList.length > 0) {
                return this.latestCtVersionFields;
            }

            return this.filteredSortedDocumentList.flatMap(doc => doc.Fields);
        },
        latestCtVersionFields() {
            if (this.filteredSortedDocumentList.length === 0) return [];
            const latestCtVersion = this.filteredSortedDocumentList.reduce((a, b) => {
                if (a.CtMajVersion > b.CtMajVersion) return a;
                if (a.CtMajVersion < b.CtMajVersion) return b;
                if (a.CtMinVersion >= b.CtMinVersion) return a;
                return b;
            });
            return (latestCtVersion.Fields || []).filter(f => f.IsVisible);
        },
        filteredSortedDocumentList() {
            var filteredSortedList = [...this.documentList];
            //FILTER
            if (this.filterItems.length > 0) {
                filteredSortedList = [];
                var originalList = this.documentList;
                this.filterItems.forEach((filter) => {
                    var filteredDocuments = originalList.filter((d) =>
                        d.Fields.some(
                            (f) =>
                                f.Title == filter.fieldTitle &&
                                filter.fieldValues.includes(f.Value)
                        )
                    );
                    filteredDocuments.forEach((fdoc) => {
                        if (!filteredSortedList.some((d) => d.ObjectID == fdoc.ObjectID)) {
                            filteredSortedList.push(fdoc);
                        }
                    });
                });
            }
            var userDecSep = this.$store.getters['account/user'].MetricDecimalSep;
            var userThouSep = this.$store.getters['account/user'].MetricThousandSep;
            var userFormat = this.$store.getters['account/user'].MetricDateFormat;
            //SORT
            function compareDocuments(fieldTitle, order = 'asc') {
                return function innerSort(a, b) {
                    var innerUserFormat = userFormat;
                    var fieldA = a.Fields.find((f) => f.Title === fieldTitle);
                    var fieldB = b.Fields.find((f) => f.Title === fieldTitle);
                    if (!fieldA) {
                        // field doesn't exist
                        return 1;
                    }
                    if (!fieldB) {
                        // field doesn't exist
                        return -1;
                    }

                    var varA =
                        typeof fieldA.Value === 'string'
                            ? fieldA.Value.toUpperCase()
                            : fieldA.Value;
                    var varB =
                        typeof fieldB.Value === 'string'
                            ? fieldB.Value.toUpperCase()
                            : fieldB.Value;

                    if (fieldA.Type === FieldType.DateTime) {
                        if (fieldA.Code == StandardFieldCode.CreationDate) {
                            innerUserFormat = `${innerUserFormat} HH:mm`;
                        }
                        varA = Utils.date.fromUserFormat(varA, innerUserFormat);
                        varB = Utils.date.fromUserFormat(varB, innerUserFormat);
                    }
                    if (fieldA.Type === FieldType.Numeric) {
                        varA = Utils.number.fromUserFormat(varA, userDecSep, userThouSep);
                        varB = Utils.number.fromUserFormat(varB, userDecSep, userThouSep);
                    }

                    let comparison = 0;
                    if (varA > varB) {
                        comparison = 1;
                    } else if (varA < varB) {
                        comparison = -1;
                    }
                    return order === 'desc' ? comparison * -1 : comparison;
                };
            }
            this.sortItems.forEach((sort) => {
                filteredSortedList.sort(
                    compareDocuments(sort.fieldTitle, sort.sortOrder)
                );
            });
            return filteredSortedList;
        },
        groupedDocumentList() {
            if (
                !this.groupItems ||
                this.groupItems.length == 0 ||
                !this.filteredSortedDocumentList
            )
                return null;

            var groupedItems = groupDocuments(
                this.groupItems,
                0,
                this.filteredSortedDocumentList
            );

            //NESTED GROUP
            function groupDocuments(groupItems, groupLevel, documentsToGroup) {
                const groupItem = groupItems[groupLevel];
                const childGroupItem = groupItems[groupLevel + 1];
                var fieldValues = Array.from(
                    new Set(
                        documentsToGroup
                            .flatMap((f) => f.Fields)
                            .filter((f) => f.Title == groupItem.fieldTitle)
                            .map((f) => {
                                if (f.Type === FieldType.DateTime) {
                                    return f.Value
                                        .split(' ')[0];
                                }
                                return f.Value;
                            })
                    )
                );
                var grouped = fieldValues.map((fv) => {
                    var groupedDocuments = documentsToGroup.filter((d) =>
                        d.Fields.some((df) => {
                            if (df.Type === FieldType.DateTime) {
                                return df.Title == groupItem.fieldTitle && df.Value.split(' ')[0] == fv;
                            }
                            return df.Title == groupItem.fieldTitle && df.Value == fv;
                        })
                    );
                    return {
                        title: `${groupItem.fieldTitle}: ${fv}`,
                        documents: groupedDocuments,
                        children: childGroupItem
                            ? groupDocuments(groupItems, groupLevel + 1, groupedDocuments)
                            : null,
                        sumFields: groupItem.sumFields,
                        maxFields: groupItem.maxFields,
                        minFields: groupItem.minFields,
                        avgFields: groupItem.avgFields,
                        summarizeOnlySelectedDocuments:
                            groupItem.summarizeOnlySelectedDocuments,
                    };
                });

                return grouped;
            }

            return groupedItems;
        },
        ungroupedDocumentList() {
            if (!this.groupedDocumentList || this.groupedDocumentList.length === 0) {
                return this.filteredSortedDocumentList;
            } else {
                const groupedIds = new Set(
                    this.groupedDocumentList.flatMap(gi =>
                        gi.documents.map(gid => gid.ObjectID)
                    )
                );

                return this.filteredSortedDocumentList.filter(
                    d => !groupedIds.has(d.ObjectID)
                );
            }
        },
        contentTypesWithFields() {
            const contentTypeMap = new Map();
            this.documentList.forEach(doc => {
                const config = this.userColumnsOrderConfig.find(
                    ct => ct.allVersionsIds.includes(doc.ContentTypeID)
                );

                const originalId = config ? config.contentTypeId : null;

                if (!contentTypeMap.has(originalId)) {
                    contentTypeMap.set(originalId, {
                        document: doc,
                        majVersion: doc.CtMajVersion,
                        minVersion: doc.CtMinVersion
                    });
                } else {
                    const existing = contentTypeMap.get(originalId);

                    const isNewer = doc.CtMajVersion > existing.majVersion ||
                        (doc.CtMajVersion === existing.majVersion &&
                            doc.CtMinVersion > existing.minVersion);

                    if (isNewer) {
                        contentTypeMap.set(originalId, {
                            document: doc,
                            majVersion: doc.CtMajVersion,
                            minVersion: doc.CtMinVersion
                        });
                    }
                }
            });

            return Array.from(contentTypeMap.entries()).map(([originalId, item]) => {
                const doc = item.document;

                const fields = (doc.Fields || [])
                    .filter(f => f.IsVisible)
                    .map(f => ({
                        code: f.Code,
                        title: f.Title,
                        type: f.Type
                    }));

                return {
                    originalContentTypeID: originalId,
                    contentTypeId: doc.ContentTypeID,
                    name: Utils.mix.getContentTypeBaseName(doc.ContentType),
                    fields
                };
            });
        },
    },
    mounted() {
        EventBus.$on('refreshDocumentList', (documentId) => {
            this.refreshResults(documentId ? [documentId] : null);
        });
        EventBus.$on('dragAndDrop', (data) => {
            this.showDragAndDropModal(data);
        });
        EventBus.$on('selectDocument', (documentId) => {
            this.selectedDocument = documentId;
        });
        EventBus.$on('content-type-changed', (value) => {
            this.selectedContentType = value;
            this.applyContentTypeFilter();
        });
        EventBus.$on('comments-updated', this.onCommentsUpdated);
        EventBus.$on('attachments-updated', this.onAttachmentsUpdated);
        EventBus.$on('alerts-updated', this.onAlertsUpdated);
        EventBus.$on('linked-documents-updated', this.onLinkedDocumentsUpdated);
    },
    destroyed() {
        EventBus.$off('refreshDocumentList');
        EventBus.$off('dragAndDrop');
        EventBus.$off('selectDocument');
        EventBus.$off('content-type-changed');
        EventBus.$off('comments-updated', this.onCommentsUpdated);
        EventBus.$off('attachments-updated', this.onAttachmentsUpdated);
        EventBus.$off('alerts-updated', this.onAlertsUpdated);
        EventBus.$off('linked-documents-updated', this.onLinkedDocumentsUpdated);
    },
    watch: {
        selectedDocument(val) {
            if (!val || val <= 0) {
                EventBus.$emit('cleanViewer');
            }
        }
    },
    methods: {
        handlePageChange({ page, pageSize }) {
            this.pagination.page = page;
            this.pagination.pageSize = pageSize;

            if (this.pagination.pageSize !== pageSize) {
                const maxPage = Math.ceil(this.totalItems / pageSize);
                if (this.pagination.page > maxPage) {
                    this.pagination.page = maxPage || 1;
                }
            }
        },
        onDocumentUpdated(document) {
            this.reloadDocument(document);
        },
        showDragAndDropModal(data) {
            this.$confirm(this.$t('DragAndDropConfirmation'), {
                title: this.$t('Confirm'),
                icon: 'mdi-attachment',
                color: 'warning',
                width: 400,
                buttonTrueText: this.$t('Clone'),
                buttonFalseText: this.$t('AddAttachment'),
            }).then((res) => {
                const document = this.documentList.find((d) => d.ObjectID == data.documentId);
                let contentTypeId;
                if (document) contentTypeId = document.ContentTypeID;

                if (res == 1)
                    this.documentModalInstance.clone(data.documentId, data.files);
                else if (res == 2)
                    this.attachmentModalInstance.new(data.documentId, data.files, contentTypeId);
            });
        },
        onAttachmentSaved() {
            this.refreshResults();
        },
        editDocument(documentId) {
            this.documentModalInstance.edit(documentId);
        },
        showFlowDiagram(documentId) {
            DocumentService.getFlowDiagrams(documentId).then((result) => {
                this.flowDiagramModalInstance.fromDocumentList(result);
            });
        },
        signDocument(documentId) {
            this.signatureModalInstance.showModal(documentId);
        },
        onDocumentSelected(value) {
            const { isSelected, documentId } = value;

            if (isSelected) {
                this.selectedDocuments.push(documentId);
            } else {
                this.selectedDocuments = this.selectedDocuments.filter(
                    (id) => id != documentId
                );
            }
            this.validateUserPermissions();
        },
        validateUserPermissions() {
            this.canEdit = true;
            this.canDelete = true;
            var documentsSelected = this.documentList.filter(
                (document) =>
                    this.selectedDocuments.filter((id) => id == document.ObjectID)
                        .length > 0
            );
            documentsSelected.forEach((document) => {
                if (!document.Permissions.DocumentEdit) {
                    this.canEdit = false;
                }
                if (!document.Permissions.DocumentDelete) {
                    this.canDelete = false;
                }
            });
        },
        onDocumentExtend(value) {
            const { documentId } = value;

            const documentExpandIndex = this.documentList.findIndex(
                (document) => document.ObjectID === documentId
            );
            if (this.documentList[documentExpandIndex].expand) {
                this.documentList[documentExpandIndex].expand = false;
            } else {
                this.documentList[documentExpandIndex].expand = true;
            }
        },
        reloadDocument(value) {
            const { oldObjectID, newObjectID, searchAllVersions } = value;

            if (newObjectID) {
                var searchRequest = {
                    SearchType: SearchType.DocumentIDs,
                    SearchPattern: newObjectID,
                    SearchAllVersions: searchAllVersions != false,
                };
                SearchService.getSearchResults(searchRequest).then((data) => {
                    if (data.Results.length > 0) {
                        var newDoc = data.Results[0];
                        this.addStandardFields(newDoc);
                        newDoc.Fields = this.getOrderedFields(newDoc);
                        this.documentList = this.documentList.map(function (doc) {
                            return doc.ObjectID == oldObjectID ? newDoc : doc;
                        });
                        this.loadPdf(newDoc.ObjectID);
                    } else {
                        Utils.toast.error('Unauthorized');
                    }
                });
            } else if (oldObjectID) {
                this.removeDocumentFromList(oldObjectID);
            }
            //ALWAYS
            if (oldObjectID) {
                this.onDocumentSelected({ isSelected: false, documentId: oldObjectID });
            }
        },
        loadPdf(documentId) {
            this.selectedDocument = documentId;
            //CHECK READ PERMISSIONS
            var doc = this.documentList.find((d) => d.ObjectID == documentId);
            if (doc.Permissions.DocumentRead) {
                if (this.searchType == SearchType.FullText) {
                    var pattern = JSON.parse(this.searchPattern);
                    EventBus.$emit(
                        'loadDocument-with-search',
                        documentId,
                        pattern.searchPattern,
                        doc.Permissions.DocumentEdit
                    );
                } else {
                    EventBus.$emit('loadDocument', documentId, doc.Permissions.DocumentEdit);
                }
            } else {
                EventBus.$emit('cleanViewer');
            }
        },
        onSign(value) {
            const { oldObjectID, newObjectID } = value;

            this.reloadDocument({
                oldObjectID: oldObjectID,
                newObjectID: newObjectID,
            });
        },
        onDocumentBlocked(objectID) {
            this.reloadDocument({ oldObjectID: objectID, newObjectID: objectID });
        },
        onReceptionConfirmed(objectID) {
            if (this.searchType == SearchType.Distribution) {
                this.refreshResults();
            } else {
                this.reloadDocument({ oldObjectID: objectID, newObjectID: objectID });
            }
        },
        onDistributionEnded(objectID) {
            if (this.searchType == SearchType.Distribution) {
                this.refreshResults();
            } else {
                this.reloadDocument({ oldObjectID: objectID, newObjectID: objectID });
            }
        },
        onVersionDeleted(value) {
            const { oldObjectID, newObjectID } = value;

            this.reloadDocument({
                oldObjectID: oldObjectID,
                newObjectID: newObjectID,
            });
        },

        loadPlugin(value) {
            const { plugin, objectIDs } = value;

            var documents = this.documentList.filter((document) =>
                objectIDs.includes(document.ObjectID)
            );
            this.pluginLoaderModalInstance.load(plugin, documents);
        },
        selectAllDocuments(isSelected) {
            if (isSelected) {
                this.selectedDocuments = this.filteredSortedDocumentList
                    .filter(d => !d.ObjectLock || (d.ObjectLock && d.ObjectLock.DocumentDeferredProcessType < 2))
                    .map(d => d.ObjectID);
            } else {
                this.selectedDocuments = [];
            }
        },
        onCommentsUpdated({ objectID, count }) {
            const doc = this.documentList.find((d) => d.ObjectID === objectID);
            if (doc) doc.TotalComments = count;
        },
        onAttachmentsUpdated({ objectID, count }) {
            const doc = this.documentList.find((d) => d.ObjectID === objectID);
            if (doc) doc.TotalAttachments = count;
        },
        onAlertsUpdated({ objectID, count }) {
            const doc = this.documentList.find((d) => d.ObjectID === objectID);
            if (doc) doc.TotalAlerts = count;
        },
        onLinkedDocumentsUpdated({ objectID, count }) {
            const doc = this.documentList.find((d) => d.ObjectID === objectID);
            if (doc) doc.TotalLinkedDocuments = count;
        },
        removeDocumentFromList(documentId) {
            if (documentId == this.selectedDocument) {
                this.selectedDocument = -1;
            }
            this.documentList = this.documentList.filter(
                (document) => document.ObjectID != documentId
            );
            this.allDocumentList = this.allDocumentList.filter(
                (document) => document.ObjectID != documentId
            );
            this.onDocumentSelected({ isSelected: false, documentId: documentId });
        },
        refreshResults(objectIDs) {
            if (objectIDs) {
                for (let i = 0; i < objectIDs.length; i++) {
                    this.reloadDocument({
                        oldObjectID: objectIDs[i],
                        newObjectID: objectIDs[i],
                        searchAllVersions: false,
                    });
                }
            } else {
                this.selectAllDocuments(false);
                this.loading = true;
                const searchRequest = {
                    SearchType: this.searchType,
                    SearchPattern: this.searchPattern,
                    ContentTypeIDs: '',
                    SearchAllVersions: false,
                    OrderByQueryPosition: false,
                    AllowDeleted: false,
                    FieldCodes: '',
                };
                if (
                    this.searchType === SearchType.FullText ||
                    this.searchType === SearchType.AdvancedSearch ||
                    this.searchType === SearchType.DocumentIDs ||
                    this.searchType === SearchType.Alert
                ) {
                    try {
                        var pattern = JSON.parse(this.searchPattern);
                        if (pattern.searchPattern)
                            searchRequest.SearchPattern = pattern.searchPattern;
                        if (pattern.contentTypeIDs) {
                            searchRequest.ContentTypeIDs = pattern.contentTypeIDs;
                            this.contentTypeIDs = pattern.contentTypeIDs.split(',');
                        }
                        if (pattern.searchAllVersions)
                            searchRequest.SearchAllVersions = pattern.searchAllVersions;
                        if (pattern.orderByQueryPosition)
                            searchRequest.OrderByQueryPosition = pattern.orderByQueryPosition;
                        if (pattern.fieldCodes)
                            searchRequest.FieldCodes = pattern.fieldCodes;
                        if (pattern.allowDeleted)
                            searchRequest.AllowDeleted = pattern.allowDeleted;
                        if (pattern.groupPattern) {
                            this.setGroupObj(pattern.groupPattern);
                        }
                        if (pattern.sortPattern) {
                            this.setSortObj(pattern.sortPattern);
                        }
                    } catch {
                        // Ignore
                    }
                }
                SearchService.getSearchResults(searchRequest)
                    .then((data) => {
                        this.searchName = data.SearchName;
                        this.defaultVisibleFields = data.DefaultVisibleFields;
                        this.defaultTableVisibleFields = data.DefaultTableVisibleFields;
                        this.loadDocuments(data.Results);

                        if (this.searchType === SearchType.Basket) {
                            BasketService.getGroupSettingsForBasket(searchRequest.SearchPattern)
                                .then((result) => {
                                    if (result.length > 0) {
                                        const items = [];
                                        result.forEach((t) => {
                                            this.fieldList.forEach((f) => {
                                                if (f.Code == t.FieldTitle) t.FieldTitle = f.Title;
                                            });
                                            t.SumFields = this.getFieldsTitle(t.SumFields);
                                            t.MaxFields = this.getFieldsTitle(t.MaxFields);
                                            t.MinFields = this.getFieldsTitle(t.MinFields);
                                            t.AvgFields = this.getFieldsTitle(t.AvgFields);
                                            items.push(new GroupItem(t));
                                        });
                                        this.onGrouped(items);
                                    } else {
                                        this.onGrouped([]);
                                        this.selectFirstDocument();
                                    }
                                    this.loading = false;
                                });
                        } else if (this.searchType === SearchType.UserSearch) {
                            UserSearchService.getGroupAndSortSettings(searchRequest.SearchPattern)
                                .then((result) => {
                                    if (result.GroupStr) this.setGroupObj(result.GroupStr);
                                    if (result.SortStr) this.setSortObj(result.SortStr);
                                    this.loading = false;
                                    this.selectFirstDocument();
                                });
                        } else {
                            this.loading = false;
                            this.selectFirstDocument();
                        }
                    })
                    .catch(() => {
                        this.loading = false;
                    });
            }
        },

        getGroupStr() {
            let group = '';
            this.groupItems.forEach((item) => {
                group += 'FIELD:' + this.getFieldCode(item.fieldTitle);
                group += 'SUMFIELDS:' + this.getFieldsCode(item.sumFields);
                group += 'MAXFIELDS:' + this.getFieldsCode(item.maxFields);
                group += 'MINFIELDS:' + this.getFieldsCode(item.minFields);
                group += 'AVGFIELDS:' + this.getFieldsCode(item.avgFields);
                group +=
                    'SUMONLYSELECTED:' +
                    (item.summarizeOnlySelectedDocuments ? 'T' : 'F');
                group += ';';
            });
            return group;
        },
        getFieldCode(fieldTitle) {
            let fieldCode = '';
            this.fieldList.forEach((field) => {
                if (field.Title == fieldTitle) {
                    fieldCode = field.Code;
                }
            });
            return fieldCode;
        },
        getFieldsCode(fieldTitles) {
            const fieldsCodes = [];
            fieldTitles.forEach((title) => {
                this.fieldList.forEach((field) => {
                    if (
                        field.Title == title &&
                        !fieldsCodes.some((code) => code == field.Code)
                    ) {
                        fieldsCodes.push(field.Code);
                    }
                });
            });
            return fieldsCodes;
        },
        getFieldTitle(fieldCode) {
            const field = this.fieldList.find(f => f.Code === fieldCode);
            return field ? field.Title : '';
        },
        getFieldsTitle(fieldCode) {
            const fieldsTitle = [];
            fieldCode.forEach((code) => {
                this.fieldList.forEach((field) => {
                    if (field.Code == code && !fieldsTitle.includes(field.Title)) {
                        fieldsTitle.push(field.Title);
                    }
                });
            });
            return fieldsTitle;
        },
        getOrderedFields(document) {

            const config = this.userColumnsOrderConfig.find(
                c => c.allVersionsIds.includes(document.ContentTypeID)
            );

            if (!config || !config.fieldCodesWithOrder) {
                return document.Fields || [];
            }

            const fieldCodesWithOrder = config.fieldCodesWithOrder;
            const fields = document.Fields || [];

            const configuredFields = [];
            const unconfiguredFields = [];

            fields.forEach(field => {
                if (Object.hasOwn(fieldCodesWithOrder, field.Code)) {
                    configuredFields.push({
                        field,
                        order: fieldCodesWithOrder[field.Code]
                    });
                } else {
                    unconfiguredFields.push(field);
                }
            });

            configuredFields.sort((a, b) => a.order - b.order);

            return [
                ...configuredFields.map(item => item.field),
                ...unconfiguredFields
            ];
        },
        setGroupObj(groupStr) {
            this.groupStr = groupStr;
            const groupItems = groupStr.split(';');
            this.groupItems = [];
            groupItems.forEach((item) => {
                const groupItem = new GroupItem();
                const match = item.match(
                    /FIELD:(.*?)SUMFIELDS:(.*)MAXFIELDS:(.*?)MINFIELDS:(.*?)AVGFIELDS:(.*?)SUMONLYSELECTED:(.*?)/
                );
                if (match) {
                    groupItem.fieldTitle = this.getFieldTitle(match[1]);
                    groupItem.sumFields = this.getFieldsTitle(match[2].split(','));
                    groupItem.maxFields = this.getFieldsTitle(match[3].split(','));
                    groupItem.minFields = this.getFieldsTitle(match[4].split(','));
                    groupItem.avgFields = this.getFieldsTitle(match[5].split(','));
                    groupItem.summarizeOnlySelectedDocuments =
                        item.charAt(item.length - 1) === 'T';
                    this.groupItems.push(groupItem);
                }
            });
        },
        setSortObj(sortStr) {
            if (sortStr != '') {
                this.sortStr = sortStr;
            }
            const sortItems = sortStr.split(';');
            this.sortItems = [];
            sortItems.forEach((item) => {
                const sortItem = new SortItem();
                const match = item.match(/FIELD:(.*?)ORDER:(.*)/);
                if (match) {
                    sortItem.fieldTitle = this.getFieldTitle(match[1]);
                    sortItem.sortOrder = match[2];
                    this.sortItems.push(sortItem);
                }
            });
        },
        loadDocuments(results) {
            results.forEach((document) => {
                this.addStandardFields(document);
                document.Fields = this.getOrderedFields(document);
            });

            this.allDocumentList = results;
            this.applyContentTypeFilter();

            if (this.groupStr.length > 0) {
                this.setGroupObj(this.groupStr);
            }
            if (this.sortStr.length > 0) {
                this.setSortObj(this.sortStr);
            }
        },

        applyContentTypeFilter() {
            if (!this.allDocumentList || this.allDocumentList.length === 0) {
                this.documentList = [];
                return;
            }

            if (this.selectedContentType != null && this.selectedContentType !== '') {
                this.documentList = this.allDocumentList.filter(doc => {
                    const baseName = Utils.mix.getContentTypeBaseName(doc.ContentType);
                    return baseName === this.selectedContentType;
                });
                this.$refs.tableViewContainer.resetPagination();

                let grouped = [];
                let sorted = [];
                if (this.hasMultipleContentTypes()) {
                    const standardFields = this.$store.getters['account/searchStandardFields'];
                    grouped = this.groupItems.filter(gi => standardFields.some(sf => sf.Title == gi.fieldTitle));
                    sorted = this.sortItems.filter(si => standardFields.some(sf => sf.Title == si.fieldTitle));
                } else {
                    grouped = this.groupItems;
                    sorted = this.sortItems;
                }

                this.onGrouped(grouped);
                this.onSorted(sorted);
                this.onFiltered([]);
                this.clearFilter = true;

                this.selectFirstDocument();
            } else {
                this.documentList = this.allDocumentList;
            }
        },

        hasMultipleContentTypes() {
            const baseNames = new Set();
            for (const doc of this.allDocumentList) {
                const baseName = Utils.mix.getContentTypeBaseName(doc.ContentType);
                baseNames.add(baseName);
                if (baseNames.size > 1) return true;
            }
            return false;
        },

        selectFirstDocument() {
            if (this.documentList.length > 0) {
                this.loadPdf(this.documentList[0].ObjectID);
                if (this.selectedDocument === -1)
                    this.selectedDocument = this.documentList[0].ObjectID;
            } else {
                EventBus.$emit('cleanViewer');
            }
        },
        addStandardFields(document) {
            var standardFields = this.$store.getters['account/searchStandardFields'];
            standardFields.forEach((sf) => {
                var fieldCode = sf.Code;
                var fieldTitle = sf.Title;
                var fieldType = sf.Type;
                var fieldValue = '';
                switch (sf.Code) {
                case StandardFieldCode.CreationDate:
                    fieldValue = document.CreationDate;
                    break;
                case StandardFieldCode.Author:
                    fieldValue = document.Author;
                    break;
                case StandardFieldCode.DocumentId:
                    fieldValue = document.ObjectID;
                    break;
                }
                if (fieldValue != '') {
                    document.Fields.push({
                        Title: fieldTitle,
                        Code: fieldCode,
                        Type: fieldType,
                        Value: fieldValue,
                        IsVisible: true,
                    });
                }
            });
        },
        onValidate() {
            if (this.selectedDocuments.length == 0) {
                Utils.toast.warning(this.$t('DocumentSelectionError'));
                return;
            }
            this.$confirm(this.$t('DocumentsValidationConfirm'), {
                title: this.$t('Validate'),
                icon: 'mdi-warning',
                color: 'green',
                buttonTrueText: this.$t('Validate'),
                buttonFalseText: this.$t('Cancel'),
            }).then((res) => {
                if (res == 1) {
                    var selectedDocuments = this.documentList.filter((d) =>
                        this.selectedDocuments.includes(d.ObjectID)
                    );
                    this.validationModalInstance.new(selectedDocuments, 'V');
                }
            });
        },
        onRefuse() {
            if (this.selectedDocuments.length == 0) {
                Utils.toast.warning(this.$t('DocumentSelectionError'));
                return;
            }

            this.$confirm(this.$t('DocumentsRefuseConfirm'), {
                title: this.$t('Refuse'),
                icon: 'mdi-warning',
                color: 'red',
                buttonTrueText: this.$t('Refuse'),
                buttonFalseText: this.$t('Cancel'),
            }).then((res) => {
                if (res == 1) {
                    var selectedDocuments = this.documentList.filter((d) =>
                        this.selectedDocuments.includes(d.ObjectID)
                    );
                    this.validationModalInstance.new(selectedDocuments, 'R');
                }
            });
        },
        onGroup() {
            if (this.groupItems.length > 0) {
                const cloned = this.groupItems.map((item) => {
                    return Utils.object.clone(new GroupItem(), item);
                });
                this.showGroup = true;
                this.$nextTick(() => {
                    this.groupModalInstance.loadGroupItems(cloned);
                });
            } else {
                this.showGroup = true;
            }
        },
        hideGroup() {
            this.showGroup = false;
        },
        group() {
            this.groupModalInstance.onAccept();
            this.hideGroup();
        },
        onGrouped(groupItems) {
            this.groupItems = groupItems;
            this.renderTrigger = Math.random();
        },
        onFilter(fieldToFilter) {
            this.showFilter = true;
            if (fieldToFilter) {
                this.$nextTick(() => this.filterModalInstance.addFieldToFilter(fieldToFilter));
            }
        },
        hideFilter() {
            this.clearFilter = false;
            this.showFilter = false;
            this.filterModalInstance.reset();
        },
        filter() {
            const filtered = this.filterModalInstance.onFiltered();
            if (!filtered)
                return;
            this.hideFilter();
            this.renderTrigger = Math.random();
        },
        onFiltered(filterItems) {
            this.filterItems = filterItems;
        },
        onSort() {
            this.showSort = true;
            this.$nextTick(() => {
                if (this.sortItems.length > 0) {
                    this.sortModalInstance.load(this.sortItems);
                }
            });
        },
        onSortTableViewHeader(fieldToSort) {
            if (this.sortItems.length === 0) {
                this.sortItems.push({
                    fieldTitle: fieldToSort.title,
                    sortOrder: fieldToSort.sortOrder
                });
                return;
            }
            const sortItem = this.sortItems[0];

            if (sortItem.fieldTitle === fieldToSort.title) {
                sortItem.sortOrder = sortItem.sortOrder === SortType.Ascendent
                    ? SortType.Descendent
                    : SortType.Ascendent;
            } else {
                sortItem.fieldTitle = fieldToSort.title;
                sortItem.sortOrder = fieldToSort.sortOrder;
            }
        },
        hideSort() {
            this.showSort = false;
        },
        sort() {
            this.sortModalInstance.onSorted();
            this.hideSort();
        },
        onSorted(sortItems) {
            this.sortItems = sortItems;
            this.renderTrigger = Math.random();
        },
        onInternalDistributeSelected() {
            if (this.selectedDocuments.length == 0) {
                Utils.toast.warning(this.$t('DocumentSelectionError'));
                return;
            }
            var documents = this.documentList.filter((document) =>
                this.selectedDocuments.includes(document.ObjectID)
            );
            this.distributionModalInstance.new(documents, true);
        },
        onExternalDistributeSelected() {
            if (this.selectedDocuments.length == 0) {
                Utils.toast.warning(this.$t('DocumentSelectionError'));
                return;
            }
            //ExternalDistributionNotAllowed
            var notAllowed = this.documentList
                .filter(
                    (document) =>
                        this.selectedDocuments.includes(document.ObjectID) &&
                        !document.Permissions.DocumentExternalDistribution
                )
                .map(function (document) {
                    return document.ObjectID;
                })
                .join(',');

            if (notAllowed) {
                var message = this.$t('ExternalDistributionNotAllowed').replace(
                    '{0}',
                    notAllowed
                );
                Utils.toast.error(message);
            } else {
                var documents = this.documentList.filter((document) =>
                    this.selectedDocuments.includes(document.ObjectID)
                );
                this.distributionModalInstance.new(documents, false);
            }
        },
        onExportSelected() {
            if (this.selectedDocuments.length == 0) {
                Utils.toast.warning(this.$t('DocumentSelectionError'));
                return;
            }
            DocumentService.export(this.selectedDocuments);
        },
        onDeleteSelected() {
            if (this.selectedDocuments.length == 0) {
                Utils.toast.warning(this.$t('DocumentSelectionError'));
                return;
            }
            this.$confirm(this.$t('DeleteDocumentsConfirmation'), {
                title: this.$t('Delete'),
                icon: 'mdi-warning',
                color: 'warning',
                buttonTrueText: this.$t('Delete'),
                buttonFalseText: this.$t('Cancel'),
            }).then((res) => {
                if (res == 1) {
                    DocumentService.remove(this.selectedDocuments).then((result) => {
                        Utils.toast.success(this.$t('DocumentsDeletedSuccessfully'));
                        this.selectedDocuments.forEach((documentId) => {
                            this.removeDocumentFromList(documentId);
                        });

                        this.selectedDocuments = [];
                    });
                }
            });
        },
        onDownloadSelected() {
            if (this.selectedDocuments.length == 0) {
                Utils.toast.warning(this.$t('DocumentSelectionError'));
                return;
            }
            var documentsToDownload = this.documentList
                .filter(
                    (document) =>
                        this.selectedDocuments.includes(document.ObjectID) &&
                        document.Permissions.DocumentDownload
                )
                .map((d) => d.ObjectID);

            if (documentsToDownload.length == 0) {
                Utils.toast.error(this.$t('DocumentsNotAllowedToDownload'));
                return;
            }
            DocumentService.downloadSelected(documentsToDownload);
        },
        onEditSelected() {
            if (this.selectedDocuments.length == 0) {
                Utils.toast.warning(this.$t('DocumentSelectionError'));
                return;
            }

            var documents = this.documentList.filter(
                (document) =>
                    this.selectedDocuments.filter((id) => id == document.ObjectID)
                        .length > 0
            );
            var contentTypeList = [];
            documents.forEach((document) => {
                var exist =
                    contentTypeList.filter(
                        (contentType) => contentType.id == document.ContentTypeID
                    ).length > 0;
                if (!exist) {
                    contentTypeList.push({
                        id: document.ContentTypeID,
                        name: document.ContentType,
                        loading: false,
                        document: null,
                    });
                }
            });

            var documentList = documents.map(function (document) {
                return { id: document.ObjectID, contentTypeId: document.ContentTypeID };
            });
            if (documentList.length === 1) {
                this.documentModalInstance.edit(documentList[0].id);
            } else {
                this.multipleEditionModalInstance.showModal(
                    documentList,
                    contentTypeList
                );
            }
        },
        editSearch() {
            var pattern = JSON.parse(this.searchPattern);
            const dto = new UserSearch();
            if (pattern.searchPattern) dto.FilterStr = pattern.searchPattern;
            if (pattern.contentTypeIDs) dto.ContentTypeIDs = pattern.contentTypeIDs;
            if (pattern.searchAllVersions) dto.SearchAllVersions = pattern.searchAllVersions;
            if (pattern.groupPattern) dto.GroupStr = pattern.groupPattern;
            if (pattern.sortPattern) dto.SortStr = pattern.sortPattern;
            this.advSearchModalInstance.edit(dto);
        },
        onRetryProcess(documentId) {
            this.$confirm(this.$t('RetryProcessConfirm'), {
                title: this.$t('RetryDifferredProcess'),
                icon: 'mdi-refresh',
                color: 'primary',
                buttonTrueText: this.$t('Retry'),
                buttonFalseText: this.$t('Cancel'),
            }).then((res) => {
                if (res == 1) {
                    DocumentService.retrySignature(documentId)
                        .finally(() => {
                            this.refreshResults([documentId]);
                        });
                }
            });
        },
        onConfigureFieldsOrder() {
            if (this.documentList.length === 0) {
                Utils.toast.warning(this.$t('NoDocumentsFound'));
                return;
            }
            this.fieldOrderConfigModalInstance.show();
        },
        hideFieldOrderConfig() {
            this.fieldOrderConfigModalInstance.hide();
        },
    },
};
</script>
<style scoped>
.toolbar-fixed {
  position: sticky;
  top: 0;
  z-index: 5;
}
</style>
